import { useState, useEffect } from 'react'
import { useSwipeable } from 'react-swipeable'
import { apiClient } from '../api/client'
import { imageCache } from '../utils/imageCache'
import type { Comment, CommentImage } from '../api/types'

type Props = {
  comment: Comment
  onClick: (image: CommentImage) => void
}

/**
 * Instagram風コメントカード
 *
 * フィーチャー画像を大きく表示し、複数画像の場合はカルーセルで切り替え。
 * オーバーレイでメタデータを表示し、ぼかしアップローディングを実装。
 */
export function CommentCard({ comment, onClick }: Props) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [currentBlobUrl, setCurrentBlobUrl] = useState<string>('')
  const [error, setError] = useState(false)

  // 現在のインデックスの画像を読み込む
  useEffect(() => {
    let cancelled = false

    const loadCurrentImage = async () => {
      // 画像がない場合
      if (comment.images.length === 0) {
        return
      }

      // 現在のインデックスの画像を取得
      const currentImage = comment.images[currentImageIndex]
      if (!currentImage) {
        return
      }

      try {
        setImageLoaded(false)
        setError(false)

        // グローバルキャッシュを使用
        const blobUrl = await imageCache.get(currentImage.key, () =>
          apiClient.fetchImageBlob(currentImage.key)
        )

        if (!cancelled) {
          setCurrentBlobUrl(blobUrl)
        }
      } catch (err) {
        console.error(`Failed to load image ${currentImage.key}:`, err)
        if (!cancelled) {
          setError(true)
        }
      }
    }

    loadCurrentImage()

    // クリーンアップ
    return () => {
      cancelled = true
    }
  }, [comment.images, currentImageIndex])

  // カルーセルハンドラー
  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation()
    setCurrentImageIndex((prev) => (prev + 1) % comment.images.length)
  }

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation()
    setCurrentImageIndex(
      (prev) => (prev - 1 + comment.images.length) % comment.images.length
    )
  }

  const goToImage = (index: number) => (e: React.MouseEvent) => {
    e.stopPropagation()
    setCurrentImageIndex(index)
  }

  // 画像クリックハンドラー
  const handleImageClick = () => {
    if (comment.images[currentImageIndex]) {
      onClick(comment.images[currentImageIndex]!)
    }
  }

  // スワイプハンドラー
  const swipeHandlers = useSwipeable({
    onSwipedLeft: (e) => {
      e.event.stopPropagation()
      setCurrentImageIndex((prev) => (prev + 1) % comment.images.length)
    },
    onSwipedRight: (e) => {
      e.event.stopPropagation()
      setCurrentImageIndex(
        (prev) => (prev - 1 + comment.images.length) % comment.images.length
      )
    },
    preventScrollOnSwipe: false,
    trackTouch: true,
    delta: 30,
    swipeDuration: 300,
  })

  // 日付フォーマット
  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr)
      return date.toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    } catch {
      return dateStr
    }
  }

  // 画像がない場合
  if (comment.images.length === 0) {
    return (
      <div className="comment-card">
        <div className="comment-images-empty">画像なし</div>
        {comment.notes && <div className="card-notes">{comment.notes}</div>}
        <p className="card-date">{formatDate(comment.date)}</p>
      </div>
    )
  }

  return (
    <div className="comment-card">
      {/* フィーチャー画像コンテナ */}
      <div
        className="card-image-container"
        onClick={handleImageClick}
        {...swipeHandlers}
      >
        {/* ぼかしプレースホルダー */}
        {!imageLoaded && !error && (
          <div className="image-placeholder blur" />
        )}

        {/* フィーチャー画像 */}
        {!error && currentBlobUrl && (
          <img
            src={currentBlobUrl}
            className={`card-featured-image ${imageLoaded ? 'loaded' : ''}`}
            onLoad={() => setImageLoaded(true)}
            alt={`${comment.memberName} - ${formatDate(comment.date)}`}
            loading="lazy"
          />
        )}

        {/* エラー表示 */}
        {error && (
          <div className="image-error">画像の読み込みに失敗しました</div>
        )}

        {/* カルーセルコントロール（複数画像の場合のみ） */}
        {comment.images.length > 1 && (
          <div className="carousel-indicators">
            {/* 前へボタン */}
            <button
              className="carousel-button carousel-prev"
              onClick={prevImage}
              aria-label="前の画像"
            >
              ←
            </button>

            {/* ドットインジケーター */}
            <div className="carousel-dots">
              {comment.images.map((_, i) => (
                <span
                  key={i}
                  className={`dot ${i === currentImageIndex ? 'active' : ''}`}
                  onClick={goToImage(i)}
                  aria-label={`画像 ${i + 1}`}
                />
              ))}
            </div>

            {/* 次へボタン */}
            <button
              className="carousel-button carousel-next"
              onClick={nextImage}
              aria-label="次の画像"
            >
              →
            </button>
          </div>
        )}

      </div>

      {/* ノートと日付 */}
      {comment.notes && <div className="card-notes">{comment.notes}</div>}
      <p className="card-date">{formatDate(comment.date)}</p>
    </div>
  )
}
